<template>
  <section class="boost-preview" :class="{ 'boost-preview--eligible': eligible }" :aria-label="$t('Last Minute Boost')">
    <div class="boost-preview__header">
      <div class="boost-preview__title">
        <span class="boost-preview__icon" aria-hidden="true"><i class="fas fa-bolt"></i></span>
        <div><span class="boost-preview__eyebrow">{{ $t("Automatic activation") }}</span><h3>{{ $t("Last Minute Boost") }}</h3></div>
      </div>
      <span class="boost-preview__badge"><i :class="eligible ? 'fas fa-check-circle' : 'far fa-clock'" aria-hidden="true"></i> {{ $t(eligible ? "Eligible for boost" : validDeadline ? "Not enabled" : "Select a deadline") }}</span>
    </div>
    <p class="boost-preview__intro">{{ $t("Boost activates automatically 24 hours before each shift starts, if it remains unclaimed. Claims must still be open.") }}</p>
    <div class="boost-preview__summary" role="status" aria-live="polite" aria-atomic="true">
      <strong>{{ headline }}</strong>
      <span>{{ explanation }}</span>
    </div>
    <div class="boost-preview__timeline" aria-hidden="true">
      <div class="boost-preview__labels"><span>{{ $t("24h before start") }}</span><span>{{ $t("Shift start") }}</span></div>
      <div class="boost-preview__track"><div class="boost-preview__window" :style="{ width: `${eligible ? windowHours / 24 * 100 : 0}%` }"></div></div>
      <div class="boost-preview__caption">{{ $t(eligible ? "Highlighted: boost window until your claim deadline" : "Your claim deadline leaves no boost window") }}</div>
    </div>
    <div v-if="showChoices" class="boost-preview__choices">
      <span>{{ $t("Choose a boost-eligible deadline") }}</span>
      <div class="boost-preview__buttons">
        <button v-for="hours in [12, 6, 3, 1, 0]" :key="hours" type="button" :aria-pressed="deadlineHours === hours" @click="$emit('select-hours', hours)">
          {{ hours === 0 ? $t("At shift start") : $t("{hours}h before start", {hours}) }}
          <small>{{ $t("{hours}h boost window", {hours: 24 - hours}) }}</small>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "LastMinuteBoostPreview",
  props: {
    deadlineHours: {type: Number, default: null},
    showChoices: {type: Boolean, default: true}
  },
  emits: ["select-hours"],
  computed: {
    validDeadline() {
      return this.deadlineHours !== null && Number.isFinite(this.deadlineHours) && this.deadlineHours >= 0
    },
    eligible() {
      return this.validDeadline && this.deadlineHours < 24
    },
    windowHours() {
      return Math.round((24 - this.deadlineHours) * 100) / 100
    },
    headline() {
      if (!this.validDeadline) return this.$t("Choose a claim deadline to see boost eligibility")
      if (this.eligible) return this.$t("Up to {hours} hours of boost per shift", {hours: this.windowHours})
      if (this.deadlineHours === 24) return this.$t("Your deadline meets the 24-hour threshold")
      return this.$t("Your deadline closes {hours} hours before boost begins", {hours: Math.round((this.deadlineHours - 24) * 100) / 100})
    },
    explanation() {
      return this.$t(this.eligible
        ? "Enabled by your deadline selection. Boost can run from 24 hours before the shift until the claim deadline, while unclaimed."
        : "Select a deadline less than 24 hours before shift start to allow automatic boost activation.")
    }
  }
}
</script>

<style scoped>
.boost-preview { --boost-accent: #526581; margin: 0 0 1.75rem; padding: 1.5rem; border: 1px solid var(--care-line); border-radius: var(--care-card-radius); background: var(--care-header-surface); color: var(--care-ink); text-align: start; }
.boost-preview--eligible { --boost-accent: #147762; border-color: var(--care-success-line); background: var(--care-success-surface); }
.boost-preview__header, .boost-preview__title { display: flex; align-items: center; gap: 12px; }
.boost-preview__header { justify-content: space-between; flex-wrap: wrap; }
.boost-preview__icon { display: grid; place-items: center; width: 44px; height: 44px; border-radius: 12px; background: #fff; border: 1px solid var(--care-line); color: var(--boost-accent); }
.boost-preview__eyebrow { display: block; font-size: 10px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: var(--boost-accent); }
.boost-preview h3 { margin: 3px 0 0; font-size: 18px; font-weight: 700; color: var(--care-ink); }
.boost-preview__badge { padding: 6px 10px; border-radius: 24px; background: #fff; border: 1px solid var(--care-line); color: var(--boost-accent); font-size: 12px; font-weight: 700; }
.boost-preview__intro { margin: 16px 0; font-size: 13px; line-height: 1.65; color: var(--care-muted); }
.boost-preview__summary { display: grid; gap: 5px; font-size: 13px; line-height: 1.6; }
.boost-preview__summary strong { font-size: 16px; color: var(--boost-accent); }
.boost-preview__timeline { margin: 20px 0; }
.boost-preview__labels { display: flex; justify-content: space-between; gap: 16px; font-size: 12px; font-weight: 600; }
.boost-preview__track { height: 8px; margin: 9px 0; border-radius: 8px; background: #dfe6ee; overflow: hidden; }
.boost-preview__window { height: 100%; background: #19866e; border-radius: inherit; transition: width .2s ease; }
.boost-preview__caption { color: var(--care-muted); font-size: 11px; }
.boost-preview__choices { border-top: 1px solid var(--care-line); padding-top: 16px; font-size: 12px; font-weight: 600; }
.boost-preview__buttons { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.boost-preview__buttons button { flex: 1 1 140px; padding: 10px 12px; border: 1px solid var(--care-line); border-radius: 10px; background: #fff; color: var(--care-ink); font: inherit; text-align: start; cursor: pointer; }
.boost-preview__buttons button:hover, .boost-preview__buttons button[aria-pressed="true"] { border-color: #147762; background: #eaf7f1; color: #11634f; }
.boost-preview__buttons button:focus-visible { outline: 3px solid #527ed3; outline-offset: 3px; }
.boost-preview__buttons small { display: block; margin-top: 3px; font-size: 11px; font-weight: 400; }
@media (max-width: 575px) { .boost-preview { padding: 18px; } }
@media (prefers-reduced-motion: reduce) { .boost-preview__window { transition: none; } }
</style>
